/**
 * Progress-bar math for fingering generation.
 *
 * The backend decides how far along a job is. This file only turns that
 * fraction into a percent and creeps the bar forward between updates.
 * Run: node --experimental-strip-types --test src/model/generationProgress.test.ts
 */

export type GenerationPhaseName = 'reading_music' | 'generating_fingerings';

export interface GenerationPhase {
  phase: GenerationPhaseName;
  fraction: number;
  nextFraction: number;
  stepElapsedMs: number;
  stepExpectedMs: number;
}

/** Upload is already done, so the bar starts at 5%. The last 3% is the score appearing. */
const BAR_START = 5;
const BAR_SPAN = 92;
const CREEP_HEADROOM = 0.9;
const FALLBACK_FROM = 5;
const FALLBACK_TO = 95;
const FALLBACK_EXPECTED_MS = 60_000;

export const PHASE_LABELS: Record<GenerationPhaseName, string> = {
  reading_music: 'Reading the music...',
  generating_fingerings: 'Generating fingerings...',
};

export function toPercent(fraction: number): number {
  const clamped = clamp(fraction, 0, 1);
  return BAR_START + BAR_SPAN * clamped;
}

/**
 * Move from `base` toward `cap` but never reach it. `tau` is how long the
 * step is expected to take; at that point the bar is about 63% of the way.
 */
export function creepPercent(
  base: number,
  cap: number,
  elapsedMs: number,
  tauMs: number
): number {
  if (cap <= base) return base;
  if (tauMs <= 0) return cap;
  const elapsed = Math.max(0, elapsedMs);
  return base + (cap - base) * (1 - Math.exp(-elapsed / tauMs));
}

/**
 * Where the bar should be, given the latest server view and time passed
 * since that view arrived. `null` means the backend sent no phase (an older
 * server): creep from 5% toward 95% over about a minute.
 */
export function targetPercent(
  phase: GenerationPhase | null,
  extraElapsedMs: number
): number {
  if (!phase) {
    const cap = FALLBACK_FROM + CREEP_HEADROOM * (FALLBACK_TO - FALLBACK_FROM);
    return creepPercent(
      FALLBACK_FROM,
      cap,
      extraElapsedMs,
      FALLBACK_EXPECTED_MS
    );
  }
  const base = toPercent(phase.fraction);
  const next = toPercent(phase.nextFraction);
  const cap = base + CREEP_HEADROOM * (next - base);
  const elapsed =
    Math.max(0, phase.stepElapsedMs) + Math.max(0, extraElapsedMs);
  return creepPercent(base, cap, elapsed, phase.stepExpectedMs);
}

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  return Math.min(max, Math.max(min, value));
}
